import EChartCommon, {
  getAxisPointer,
} from '@/pages/opinion/components/EChartCommon';
import React, { useMemo } from 'react';
import {
  formatTimeXAxisText,
  getChartPosition,
  setIntervalValue,
  setMinIntervalValue,
} from '@/pages/opinion/utils/chart';
import { echartsContent } from '@/pages/opinion/utils';
import {
  formatChartParamsDate,
  listTimeTitleFunc,
} from '@/pages/opinion/components/ChartCommon/formatter';
import { useIntl } from 'umi';
import { NO_DATE } from '@/pages/opinion/constants';
import {
  eChartPercentageYAxis,
  percentageFormat,
  reviewsFormat,
} from '@/pages/opinion/utils/value';
const seriesDataKeys = [
  {
    text: 'Count',
    type: 'bar',
    color: '#5086F3',
  },
  {
    text: 'Rate',
    type: 'line',
    color: '#69D19C',
  },
];
export default function BarLineEchart({ filter, data, height = 260 }: any) {
  const { formatMessage } = useIntl();

  const options = useMemo(() => {
    const legends: { name: string; itemStyle: { color: string } }[] =
      seriesDataKeys.map((item) => {
        return {
          name: item.text,
          itemStyle: { color: item.color },
        };
      });
    const series: { name: string; type: string; data: any[] }[] =
      seriesDataKeys.map((item, index) => {
        return {
          name: item.text,
          type: item.type,
          itemStyle: { color: item.color },
          data: [],
          yAxisIndex: index,
        };
      });
    let xAxis: string[] = [];

    data.forEach((item: any) => {
      series[0].data.push(item.count || 0);
      series[1].data.push(item.rate || 0);
      xAxis.push(item.time);
    });

    let currentOptions = {
      tooltip: {
        position: (
          pos: [any, any],
          params: any,
          dom: any,
          rect: any,
          size: { viewSize: [any, any] },
        ) =>
          getChartPosition(pos, params, dom, rect, size, 50, 15, 'bottom', 3),
        appendToBody: false,
        confine: true,
        formatter: (params: any) => {
          let title = listTimeTitleFunc(
            params?.[0]?.axisValue,
            filter.date_type,
            true,
          );
          return formatChartParamsDate({
            params,
            title: title,
            listTitleFunc: (params) => {
              return formatMessage({ id: params.seriesName || NO_DATE.EMPTY });
            },
            valueFormat: (value, ele) => {
              if (ele.seriesName === 'Count') {
                return reviewsFormat(value);
              }
              return percentageFormat(value * 100, '-', 2);
            },
          });
        },
      },
      axisPointer: getAxisPointer((data) =>
        formatTimeXAxisText(data.value, filter?.date_type),
      ),
      grid: {
        top: 10,
        right: 0,
        containLabel: true,
      },
      legend: {
        data: legends,
        formatter: (name: string) => {
          return formatMessage({ id: name || NO_DATE.EMPTY });
        },
      },
      xAxis: {
        boundaryGap: ['20%', 0],
        data: xAxis,
        axisLabel: {
          showMaxLabel: true,
          formatter: (value: string) =>
            formatTimeXAxisText(value, filter?.date_type),
        },
      },
      yAxis: [
        {
          type: 'value',
          axisLabel: {
            formatter: (value: any) => {
              return echartsContent(value);
            },
          },
        },
        {
          type: 'value',
          axisLabel: {
            formatter: (value: any) => {
              return eChartPercentageYAxis(value);
            },
          },
        },
      ],
      series: series,
    };
    setIntervalValue(currentOptions.series[0]?.data, currentOptions.yAxis[0]);
    setMinIntervalValue(
      currentOptions.series[1]?.data,
      currentOptions.yAxis[1],
    );

    return currentOptions;
  }, [formatMessage, filter]);

  const noData = useMemo(() => {
    return !data.some((item: any) => item.count > 0 || item.rate > 0);
  }, [data]);

  return (
    <EChartCommon
      options={options}
      height={height}
      empty={noData}
      optionsProps={{ type: 'bar' }}
      leftTitle={formatMessage({ id: 'Count' })}
      rightTitle={formatMessage({ id: 'Rate' })}
    ></EChartCommon>
  );
}
